import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, Nav, NavItem, NavLink } from "reactstrap";
import { C, tbl, selectStyles } from "../reporting/reportingStyles";
import MultiSelectField from "../common/MultiSelectField";
import {
  updateDeviceFloor,
  setPrimaryDevice,
  updateDeviceAlertSettings,
  updateDeviceChannels,
  updateDeviceVirtualLocations,
  approveDeviceKey,
  rejectDeviceKey,
  revokeDeviceKey,
} from "../../utils/apiCalls";
import { isReadOnly } from "../../constants/roles";
import {
  branchIdOf,
  channelsOf,
  deviceLabel,
  formatChannelNames,
  isReceiving,
  routingClaimsOf,
  summarizeBranch,
  virtualLocationsOf,
} from "./deviceRouting";
import {
  formatCustomDateTime,
  getTimeAgo,
  StatusPill,
  DetailRow,
  RoutingModeBadge,
  ChannelChip,
  Section,
  Notice,
  KeyStatusPill,
} from "./deviceUi";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "routing", label: "Order Routing" },
  { id: "alerts", label: "Alerts" },
  { id: "analytics", label: "Analytics" },
];

const sameIds = (a, b) => {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((x) => set.has(x));
};

const pillStyle = { fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 6, background: C.bg, color: C.textMed, border: `1px solid ${C.border}` };

/**
 * Device Details, split into tabs so routing, alerts and diagnostics each get room.
 *
 * `device` is read from the page's device list on every render, and every save writes back
 * through `onDevicesChange`, so the table and this modal never disagree.
 */
const DeviceDetailsModal = ({
  isOpen,
  toggle,
  device,
  branchDevices,
  floors,
  channelOptions,
  virtualLocationOptions,
  merchantSelected,
  onDevicesChange,
  onViewKds,
  isFetchingKds,
}) => {
  const [activeTab, setActiveTab] = useState("overview");

  // Channels
  const [channelDraft, setChannelDraft] = useState([]);
  const [isSavingChannels, setIsSavingChannels] = useState(false);

  // Deliverect virtual locations (brands), as DeliverectVirtualLocations ids
  const [brandDraft, setBrandDraft] = useState([]);
  const [isSavingBrands, setIsSavingBrands] = useState(false);

  // Stale order alerts. The mute select always opens at 0, which also means "lift the mute".
  // Without knowing whether the admin touched it, saving "mark inactive" on an already-muted
  // device would silently throw its mute away.
  const [alertMuteDays, setAlertMuteDays] = useState(0);
  const [alertMuteTouched, setAlertMuteTouched] = useState(false);
  const [alertMarkInactive, setAlertMarkInactive] = useState(false);
  const [alertReason, setAlertReason] = useState("");
  const [isSavingAlertSettings, setIsSavingAlertSettings] = useState(false);

  const [changingKey, setChangingKey] = useState(null);
  const readOnly = isReadOnly();

  const deviceId = device?.id;
  const savedChannelIds = useMemo(() => channelsOf(device).map((c) => c.id), [device]);
  const savedBrandIds = useMemo(() => virtualLocationsOf(device).map((v) => v.id), [device]);

  // Reset every draft when a different device opens, so nothing carries over from the last one.
  useEffect(() => {
    if (!isOpen || !deviceId) return;
    setActiveTab("overview");
    setChannelDraft(savedChannelIds);
    setBrandDraft(savedBrandIds);
    setAlertMuteDays(0);
    setAlertMuteTouched(false);
    setAlertMarkInactive(device?.isActive === false);
    setAlertReason("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, deviceId]);

  const patchDevice = (id, patch) =>
    onDevicesChange((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  const KEY_ACTIONS = {
    approve: { call: approveDeviceKey },
    reject: { call: rejectDeviceKey },
    revoke: {
      call: revokeDeviceKey,
      confirm: "Revoke this tablet's key? It stops working at once, and the tablet's next request waits for approval here.",
    },
  };

  const handleKeyAction = async (action) => {
    const { call, confirm } = KEY_ACTIONS[action];
    if (confirm && !window.confirm(confirm)) return;
    setChangingKey(action);
    const keyFields = await call(device.id);
    setChangingKey(null);
    if (keyFields) patchDevice(device.id, keyFields);
  };

  // ─── Channel picker options ────────────────────────────
  const nameById = useMemo(() => {
    const map = {};
    (channelOptions?.catalog || []).forEach((c) => { map[c.id] = c.name; });
    channelsOf(device).forEach((c) => { map[c.id] = map[c.id] || c.name; });
    return map;
  }, [channelOptions, device]);

  // Searchable by name; the groups only order the list.
  const groupedOptions = useMemo(() => {
    const catalog = channelOptions?.catalog || [];
    const toOption = (c) => ({ value: c.id, label: c.name });
    return [
      { label: "Saudi delivery apps", options: catalog.filter((c) => c.featured).map(toOption) },
      { label: "Other partners", options: catalog.filter((c) => c.partner).map(toOption) },
      { label: "All channels", options: catalog.filter((c) => !c.featured && !c.partner).map(toOption) },
    ].filter((g) => g.options.length > 0);
  }, [channelOptions]);

  const draftValue = channelDraft.map((id) => ({ value: id, label: nameById[id] || id }));

  // ─── Brand picker options ──────────────────────────────
  // Only active aliases routed to this tablet's branch can be claimed; a claimed one that has since
  // been switched off stays visible so it can be removed.
  const brandById = useMemo(() => {
    const map = {};
    virtualLocationsOf(device).forEach((v) => { map[v.id] = v; });
    (virtualLocationOptions || []).forEach((v) => { map[v.id] = v; });
    return map;
  }, [virtualLocationOptions, device]);

  const brandOptions = useMemo(
    () => (virtualLocationOptions || [])
      .filter((v) => v.active)
      .map((v) => ({ value: v.id, label: `${v.label} · ${v.externalLocationId}` })),
    [virtualLocationOptions]
  );

  const brandDraftValue = brandDraft.map((id) => ({
    value: id,
    label: brandById[id] ? `${brandById[id].label} · ${brandById[id].externalLocationId}` : `#${id}`,
  }));

  if (!device) return null;

  const branchId = branchIdOf(device);
  const siblings = (branchDevices || []).filter((d) => d.id !== device.id);
  const summary = summarizeBranch(branchId, device?.Branch?.name, branchDevices || [device]);
  const channelsDirty = !sameIds(channelDraft, savedChannelIds);
  const brandsDirty = !sameIds(brandDraft, savedBrandIds);

  // Brands in the draft that another tablet in this branch holds today; saving moves them here.
  const brandConflicts = brandDraft
    .map((id) => {
      const owner = siblings.find((s) => virtualLocationsOf(s).some((v) => v.id === id));
      return owner ? { id, owner } : null;
    })
    .filter(Boolean);

  // Channels in the draft that another tablet in this branch holds today; saving moves them here.
  const conflicts = channelDraft
    .map((id) => {
      const owner = siblings.find((s) => channelsOf(s).some((c) => c.id === id));
      return owner ? { id, owner } : null;
    })
    .filter(Boolean);

  // ─── Handlers ──────────────────────────────────────────
  const handleAssignFloor = async (rawFloorId) => {
    const floorId = rawFloorId === "" ? null : Number(rawFloorId);
    const result = await updateDeviceFloor(device.id, floorId);
    if (result) {
      const floor = (floors || []).find((f) => f.id === floorId);
      patchDevice(device.id, { floorId, Floor: floor ? { id: floor.id, name: floor.name } : null });
    }
  };

  const handleSetPrimary = async (isPrimary) => {
    const result = await setPrimaryDevice(device.id, isPrimary);
    if (result) {
      // One primary per branch: set this device, clear the others in the same branch.
      onDevicesChange((prev) =>
        prev.map((d) => {
          if (d.id === device.id) return { ...d, isPrimaryDevice: isPrimary };
          if (isPrimary && branchIdOf(d) === branchId) return { ...d, isPrimaryDevice: false };
          return d;
        })
      );
    }
  };

  const handleSaveChannels = async () => {
    setIsSavingChannels(true);
    try {
      const result = await updateDeviceChannels(device.id, channelDraft);
      if (!result) return;
      const released = new Map((result.releasedFrom || []).map((r) => [r.id, r]));
      onDevicesChange((prev) =>
        prev.map((d) => {
          if (d.id === device.id) {
            return { ...d, routedChannelIds: result.routedChannelIds, routedChannels: result.routedChannels || [] };
          }
          const r = released.get(d.id);
          if (!r) return d;
          return {
            ...d,
            routedChannelIds: r.routedChannelIds,
            routedChannels: channelsOf(d).filter((c) => !r.channelIds.includes(c.id)),
          };
        })
      );
      setChannelDraft((result.routedChannels || []).map((c) => c.id));
    } finally {
      setIsSavingChannels(false);
    }
  };

  const handleSaveBrands = async () => {
    setIsSavingBrands(true);
    try {
      const result = await updateDeviceVirtualLocations(device.id, brandDraft);
      if (!result) return;
      const released = new Map((result.releasedFrom || []).map((r) => [r.id, r]));
      onDevicesChange((prev) =>
        prev.map((d) => {
          if (d.id === device.id) {
            return {
              ...d,
              routedVirtualLocationIds: result.routedVirtualLocationIds,
              routedVirtualLocations: result.routedVirtualLocations || [],
            };
          }
          const r = released.get(d.id);
          if (!r) return d;
          return {
            ...d,
            routedVirtualLocationIds: r.routedVirtualLocationIds,
            routedVirtualLocations: virtualLocationsOf(d).filter((v) => !r.virtualLocationIds.includes(v.id)),
          };
        })
      );
      setBrandDraft((result.routedVirtualLocations || []).map((v) => v.id));
    } finally {
      setIsSavingBrands(false);
    }
  };

  const handleSaveAlertSettings = async () => {
    setIsSavingAlertSettings(true);
    try {
      const payload = {};
      if (alertMuteTouched) payload.muteDays = Number(alertMuteDays);
      // Only send isActive when it is actually changing: an unchanged checkbox should not
      // demand a reason for a state the device is already in.
      const wasInactive = device?.isActive === false;
      if (alertMarkInactive !== wasInactive) payload.isActive = !alertMarkInactive;
      if (alertReason.trim()) payload.reason = alertReason.trim();

      const result = await updateDeviceAlertSettings(device.id, payload);
      if (result) {
        const patch = { ...result };
        delete patch.id;
        patchDevice(device.id, patch);
        setAlertReason("");
      }
    } finally {
      setIsSavingAlertSettings(false);
    }
  };

  // ─── Tabs ──────────────────────────────────────────────
  const sa = device?.staleOrdersAlert;

  const tabLink = ({ id, label }) => {
    const dot = id === "alerts" && sa?.alertable;
    return (
      <NavItem key={id}>
        <NavLink
          onClick={() => setActiveTab(id)}
          style={{
            cursor: "pointer",
            color: activeTab === id ? C.coral : C.textMed,
            fontWeight: 700, fontSize: 13, letterSpacing: 0.3,
            border: "none",
            borderBottom: activeTab === id ? `2px solid ${C.coral}` : "2px solid transparent",
            background: "transparent",
            padding: "10px 16px",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          {label}
          {dot && <span style={{ width: 7, height: 7, borderRadius: "50%", background: C.orange }} />}
        </NavLink>
      </NavItem>
    );
  };

  const renderOverview = () => (
    <>
      <Section title="Device">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 24 }}>
          <div>
            <DetailRow icon="desktop" label="Device Name" value={device?.name} />
            <DetailRow icon="map-marker" label="Branch" value={device?.Branch?.name} />
            <DetailRow icon="user" label="Logged In User" value={device?.loggedInUserName} />
            <DetailRow icon="sign-in" label="Logged In At" value={formatCustomDateTime(device?.lastLoggedInAt)} />
          </div>
          <div>
            {/* The tablet's own last contact, which an admin action on this device does not move. */}
            <DetailRow icon="heartbeat" label="Last Heartbeat" value={formatCustomDateTime(device?.lastHeartbeatAt || device?.updatedAt)} />
            <DetailRow icon="laptop" label="IP Address" value={device?.ipAddress} />
            <DetailRow icon="barcode" label="MAC Address" value={device?.macAddress} />
            <DetailRow icon="globe" label="Online Orders" value={device?.onlineOrdersEnabled ? "Enabled" : "Disabled"} iconColor={device?.onlineOrdersEnabled ? C.green : C.textLight} />
          </div>
        </div>
        {device?.isOfflineLogin && <Notice>This tablet is signed in offline.</Notice>}
      </Section>

      <Section
        title="Device Key"
        hint="The tablet's own key for the middleware. A new tablet, or one asking for a new key, waits here until approved; until then it keeps using the company key."
      >
        <div style={{ marginBottom: 8 }}>
          <KeyStatusPill status={device?.keyStatus} pending={device?.keyPendingRequest} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 24 }}>
          <DetailRow icon="key" label="Key Issued" value={formatCustomDateTime(device?.keyIssuedAt)} />
          <DetailRow icon="clock-o" label="Key Last Used" value={formatCustomDateTime(device?.keyLastUsedAt)} />
        </div>
        {!readOnly && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            {device?.keyPendingRequest && (
              <>
                <Button
                  style={{ background: C.green, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 }}
                  disabled={!!changingKey}
                  onClick={() => handleKeyAction("approve")}
                >
                  {changingKey === "approve" ? "Approving..." : "Approve request"}
                </Button>
                <Button
                  style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }}
                  disabled={!!changingKey}
                  onClick={() => handleKeyAction("reject")}
                >
                  {changingKey === "reject" ? "Rejecting..." : "Reject request"}
                </Button>
              </>
            )}
            {(device?.keyStatus === "active" || device?.keyPendingRequest) && (
              <Button
                style={{ background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 }}
                disabled={!!changingKey}
                onClick={() => handleKeyAction("revoke")}
              >
                {changingKey === "revoke" ? "Revoking..." : "Revoke key"}
              </Button>
            )}
          </div>
        )}
      </Section>

      <Section title="Orders & Shift">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 24 }}>
          <div>
            <DetailRow
              icon="shopping-cart"
              label="Active Orders"
              value={sa
                ? `${device.activeOrdersCount ?? 0} (${sa.active.count} open, ${sa.cancelled.count} cancelled, ${sa.paidCount} paid)`
                : device?.activeOrdersCount}
            />
            <DetailRow
              icon="hourglass-end"
              label="Oldest Open Order"
              value={sa?.active?.since ? formatCustomDateTime(sa.active.since) : null}
              iconColor={sa?.active?.isStale ? C.orange : undefined}
            />
            <DetailRow icon="calendar-check-o" label="Last Cash Closing" value={formatCustomDateTime(device?.lastCashClosingAt)} />
          </div>
          <div>
            <DetailRow icon="exchange" label="Unsynced Orders" value={device?.unsyncOrdersCount} />
            <DetailRow
              icon="hourglass-end"
              label="Oldest Unsynced Order"
              value={sa?.unsynced?.since ? `${formatCustomDateTime(sa.unsynced.since)}${sa.unsynced.exact ? "" : " (approx.)"}` : null}
              iconColor={sa?.unsynced?.isStale ? C.orange : undefined}
            />
            <DetailRow icon="calendar" label="Last Span Closing" value={formatCustomDateTime(device?.lastSpanClosingAt)} />
          </div>
        </div>
      </Section>

      {/* The by-date breakdown as of the last heartbeat. The Order Statistics popup shows the
          same thing live, but only for an ONLINE tablet, which is rarely the one sitting on a
          week-old backlog. */}
      {device?.unsyncedOrdersByDate?.length > 0 && (
        <Section
          title="Unsynced Orders by Date"
          hint={`Tablet local time, as of the last heartbeat (${getTimeAgo(device?.lastHeartbeatAt || device?.updatedAt)}).`}
        >
          <div style={{ overflowX: "auto", borderRadius: 10, border: `1px solid ${C.border}` }}>
            <table style={tbl.table}>
              <thead><tr>{["Date", "Count"].map((h) => <th key={h} style={tbl.th}>{h}</th>)}</tr></thead>
              <tbody>
                {device.unsyncedOrdersByDate.map((item, i) => (
                  <tr key={i} className="rpt-row">
                    <td style={tbl.td}>{item.date}</td>
                    <td style={{ ...tbl.td, fontWeight: 700, color: C.orange }}>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </>
  );

  const renderBranchRouting = () => (
    <Section
      title="This branch at a glance"
      right={<RoutingModeBadge mode={summary.mode} />}
      hint="Who receives which online orders in this branch right now."
    >
      <div style={{ borderRadius: 10, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {summary.devices.map((d) => {
          const chans = routingClaimsOf(d);
          const receiving = isReceiving(d);
          return (
            <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 12px", borderBottom: `1px solid ${C.borderLight}`, background: d.id === device.id ? C.coralLight : C.white }}>
              <div style={{ minWidth: 160, flexShrink: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: C.text }}>
                  {deviceLabel(d)}{d.id === device.id && <span style={{ color: C.textLight, fontWeight: 600 }}> (this)</span>}
                </div>
                <div style={{ fontSize: 11, color: receiving ? C.green : C.textLight, fontWeight: 600 }}>
                  {d.isActive === false ? "Inactive" : receiving ? "Receiving online orders" : "Online orders off"}
                </div>
              </div>
              <div style={{ flex: 1, display: "flex", flexWrap: "wrap", alignItems: "center" }}>
                {chans.map((c) => <ChannelChip key={c.id} name={c.name} muted={!receiving} />)}
                {d.isPrimaryDevice && <span style={{ ...pillStyle, color: C.blue, background: C.blueLight, borderColor: C.blueLight, marginRight: 4 }}>Primary</span>}
                {d.Floor?.name && <span style={{ ...pillStyle, marginRight: 4 }}>Floor: {d.Floor.name}</span>}
                {!chans.length && !d.isPrimaryDevice && !d.Floor?.name && <span style={{ fontSize: 12, color: C.textLight }}>No routing role</span>}
              </div>
            </div>
          );
        })}
        <div style={{ padding: "10px 12px", fontSize: 12, color: C.textMed, background: C.bg }}>
          Aggregator orders on <strong>unclaimed virtual locations and channels</strong>, and other online orders such as the pickup app, go to <strong>{summary.fallbackText}</strong>.
        </div>
      </div>
      {summary.issues.map((issue) => <Notice key={issue}>{issue}</Notice>)}
    </Section>
  );

  const renderBrands = () => (
    <Section
      title="Deliverect virtual locations"
      hint="Orders from these Deliverect locations come only to this tablet, even when another tablet claims their sales channel. Each location can belong to one tablet per branch."
      right={brandDraft.length > 0 && (
        <Button size="sm" color="link" style={{ color: C.textMed, padding: 0, fontSize: 12 }} onClick={() => setBrandDraft([])}>
          Clear all
        </Button>
      )}
    >
      {!merchantSelected ? (
        <span style={{ fontSize: 12, color: C.textLight }}>Select a merchant in the header to route virtual locations.</span>
      ) : brandOptions.length === 0 && brandDraft.length === 0 ? (
        <span style={{ fontSize: 12, color: C.textLight }}>
          No active virtual locations send orders to this branch. Add them under Merchants → Deliverect Locations.
        </span>
      ) : (
        <>
          <MultiSelectField
            options={brandOptions}
            value={brandDraftValue}
            onChange={(vals) => setBrandDraft((vals || []).map((v) => v.value))}
            placeholder="Search locations…"
            styles={selectStyles}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          />
          {brandConflicts.map(({ id, owner }) => (
            <Notice key={id}>
              <strong>{brandById[id]?.label || `#${id}`}</strong> is on {deviceLabel(owner)} now. Saving moves it to this tablet.
            </Notice>
          ))}
          {brandDraft.some((id) => brandById[id] && brandById[id].active === false) && (
            <Notice>A switched-off location cannot be saved here. Remove it, or switch it on under Deliverect Locations.</Notice>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 12 }}>
            {brandsDirty && <span style={{ fontSize: 12, color: C.orange, fontWeight: 600 }}>Unsaved changes</span>}
            {brandsDirty && (
              <Button size="sm" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, fontWeight: 600 }} onClick={() => setBrandDraft(savedBrandIds)}>
                Reset
              </Button>
            )}
            <Button
              size="sm"
              style={{ background: C.coral, border: "none", borderRadius: 8, fontWeight: 600, padding: "6px 16px" }}
              disabled={!brandsDirty || isSavingBrands}
              onClick={handleSaveBrands}
            >
              {isSavingBrands ? "Saving..." : "Save locations"}
            </Button>
          </div>
        </>
      )}
    </Section>
  );

  const renderRouting = () => (
    <>
      {renderBranchRouting()}

      {renderBrands()}

      <Section
        title="Aggregator channels"
        hint="Deliverect and Blend orders from these channels come only to this tablet. Each channel can belong to one tablet per branch."
        right={channelDraft.length > 0 && (
          <Button size="sm" color="link" style={{ color: C.textMed, padding: 0, fontSize: 12 }} onClick={() => setChannelDraft([])}>
            Clear all
          </Button>
        )}
      >
        <MultiSelectField
          options={groupedOptions}
          value={draftValue}
          onChange={(vals) => setChannelDraft((vals || []).map((v) => v.value))}
          placeholder="Search channels, e.g. Jahez, HungerStation…"
          styles={selectStyles}
          menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        />

        {conflicts.map(({ id, owner }) => (
          <Notice key={id}>
            <strong>{nameById[id] || id}</strong> is on {deviceLabel(owner)} now. Saving moves it to this tablet.
          </Notice>
        ))}
        {!device?.onlineOrdersEnabled && (
          <Notice>
            Online orders are off on this tablet (Odoo tablet setting). Its channels have no effect until that is turned on. Their orders go to {summary.fallbackText} meanwhile.
          </Notice>
        )}
        {device?.isActive === false && (
          <Notice>This tablet is marked inactive, so its channels have no effect until it checks in again.</Notice>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, marginTop: 12 }}>
          {channelsDirty && <span style={{ fontSize: 12, color: C.orange, fontWeight: 600 }}>Unsaved changes</span>}
          {channelsDirty && (
            <Button size="sm" style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, fontWeight: 600 }} onClick={() => setChannelDraft(savedChannelIds)}>
              Reset
            </Button>
          )}
          <Button
            size="sm"
            style={{ background: C.coral, border: "none", borderRadius: 8, fontWeight: 600, padding: "6px 16px" }}
            disabled={!channelsDirty || isSavingChannels}
            onClick={handleSaveChannels}
          >
            {isSavingChannels ? "Saving..." : "Save channels"}
          </Button>
        </div>
      </Section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
        <Section
          title="Primary device"
          hint="Receives online orders that no channel or floor claims, such as pickup-app orders. Without a primary, they go to every online tablet."
          style={{ marginBottom: 0 }}
        >
          <Label check style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <Input
              type="checkbox"
              checked={!!device?.isPrimaryDevice}
              onChange={(e) => handleSetPrimary(e.target.checked)}
              style={{ position: "static", margin: 0 }}
            />
            <span>Set as branch primary device</span>
          </Label>
        </Section>

        <Section
          title="Floor"
          hint="Waiter and table orders for this floor route to this tablet."
          style={{ marginBottom: 0 }}
        >
          {(floors || []).length > 0 ? (
            <Input type="select" value={device?.floorId ?? ""} onChange={(e) => handleAssignFloor(e.target.value)}>
              <option value="">— None —</option>
              {floors.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </Input>
          ) : (
            <span style={{ fontSize: 12, color: C.textLight }}>No floors configured for this branch.</span>
          )}
        </Section>
      </div>
    </>
  );

  const renderAlerts = () => {
    const mutedNow = sa?.suppressed === "muted";
    const inactive = device?.isActive === false;
    return (
      <Section
        title="Stale Order Alerts"
        hint={sa?.enabled
          ? `Emails the merchant's support users every 12 hours while this tablet holds unsynced or open orders older than ${sa.thresholdDays} day(s). Cancelled orders never trigger it.`
          : "Off for this merchant. Set a threshold in Edit Merchant → Stale Device Orders Alert (days)."}
      >
        <div style={{
          fontSize: 12, fontWeight: 600, marginBottom: 12, padding: "8px 12px", borderRadius: 8,
          background: inactive || mutedNow ? C.bg : C.greenLight,
          color: inactive || mutedNow ? C.textMed : C.green,
          border: `1px solid ${C.borderLight}`,
        }}>
          {inactive
            ? `Inactive since ${formatCustomDateTime(device?.inactiveAt)}${device?.inactiveBy ? ` by ${device.inactiveBy}` : ""}${device?.inactiveReason ? ` — ${device.inactiveReason}` : ""}`
            : mutedNow
              ? `Muted until ${formatCustomDateTime(device?.staleAlertsMutedUntil)}${device?.staleAlertsMutedBy ? ` by ${device.staleAlertsMutedBy}` : ""}${device?.staleAlertsMuteReason ? ` — ${device.staleAlertsMuteReason}` : ""}`
              : "Alerts active"}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div style={{ minWidth: 150 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>Mute alerts</div>
            <Input type="select" value={alertMuteDays} onChange={(e) => { setAlertMuteDays(Number(e.target.value)); setAlertMuteTouched(true); }}>
              <option value={0}>{mutedNow ? "Lift the mute" : "Off"}</option>
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </Input>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.textLight, marginBottom: 4 }}>Reason</div>
            <Input type="text" placeholder="e.g. branch closed for renovation" value={alertReason} onChange={(e) => setAlertReason(e.target.value)} />
          </div>
          <Button
            style={{ background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 }}
            disabled={isSavingAlertSettings}
            onClick={handleSaveAlertSettings}
          >
            {isSavingAlertSettings ? "Saving..." : "Save"}
          </Button>
        </div>

        <div style={{ marginTop: 12 }}>
          <Label check style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <Input type="checkbox" checked={alertMarkInactive} onChange={(e) => setAlertMarkInactive(e.target.checked)} style={{ position: "static", margin: 0 }} />
            <span>Mark tablet inactive (retired / not in use)</span>
          </Label>
          <div style={{ fontSize: 11, color: C.textLight, marginTop: 4 }}>
            A heartbeat from this tablet will reactivate it automatically. A reason is required to mute or deactivate. An inactive tablet's channels stop counting, so their orders fall back to the primary.
          </div>
        </div>
      </Section>
    );
  };

  const renderAnalytics = () => {
    let snapshot = null;
    let snapshotError = false;
    if (device?.lastSnapshotData) {
      try { snapshot = JSON.parse(device.lastSnapshotData); } catch { snapshotError = true; }
    }
    const battery = device?.batteryLevel;
    return (
      <>
        <Section title="Device Analytics">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 24 }}>
            <div>
              <DetailRow icon="clock-o" label="Last Order At" value={formatCustomDateTime(device?.lastOrderAt)} />
              <DetailRow icon="mobile" label="Device Model" value={device?.deviceModel} />
              <DetailRow icon="android" label="OS Version" value={device?.osVersion} />
              <DetailRow icon="battery-half" label="Battery Level" value={battery != null ? `${Math.round(battery * 100)}%` : "N/A"} iconColor={battery > 0.5 ? C.green : battery > 0.2 ? C.orange : C.coral} />
              <DetailRow icon="calendar" label="App Installed At" value={formatCustomDateTime(device?.appInstalledAt)} />
            </div>
            <div>
              <DetailRow icon="shopping-bag" label="Total Orders Today" value={device?.totalOrdersToday ?? "N/A"} />
              <DetailRow icon="hourglass-half" label="App Uptime" value={device?.appUptime} />
              <DetailRow icon="wifi" label="Network Type" value={device?.networkType} />
              <DetailRow icon="signal" label="WiFi Signal" value={device?.wifiSignalStrength != null ? `${device.wifiSignalStrength}%` : "N/A"} />
              <DetailRow icon="hdd-o" label="Free Disk Space" value={device?.freeDiskSpace} />
            </div>
          </div>
        </Section>

        {snapshotError && <Notice tone="info">Unable to parse the reinstall snapshot.</Notice>}
        {snapshot && (
          <Section title="Last Snapshot Before Reinstall" hint={formatCustomDateTime(device?.lastSnapshotAt)}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", columnGap: 24 }}>
              <div>
                <DetailRow icon="calendar" label="Previous Install Date" value={formatCustomDateTime(device?.previousInstallAt)} iconColor={C.coral} />
                <DetailRow icon="shopping-cart" label="Active Orders" value={snapshot?.activeOrdersCount ?? "N/A"} iconColor={C.coral} />
                <DetailRow icon="exchange" label="Unsynced Orders" value={snapshot?.unsyncOrdersCount ?? "N/A"} iconColor={C.coral} />
              </div>
              <div>
                <DetailRow icon="user" label="Logged In User" value={snapshot?.loggedInUserName} iconColor={C.coral} />
                <DetailRow icon="laptop" label="IP Address" value={snapshot?.ipAddress} iconColor={C.coral} />
                <DetailRow icon="battery-half" label="Battery Level" value={snapshot?.batteryLevel != null ? `${Math.round(snapshot.batteryLevel * 100)}%` : "N/A"} iconColor={C.coral} />
              </div>
            </div>
          </Section>
        )}
      </>
    );
  };

  const confirmClose = () => {
    if ((channelsDirty || brandsDirty) && !window.confirm("Discard unsaved routing changes?")) return;
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={confirmClose} size="lg" centered scrollable>
      <ModalHeader toggle={confirmClose} style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: C.text, fontSize: 16 }}>{deviceLabel(device)}</span>
          <span style={pillStyle}>#{device.id}</span>
          <StatusPill connected={device.isConnected} offlineLogin={device?.isOfflineLogin} />
          {device?.Branch?.name && <span style={pillStyle}>{device.Branch.name}</span>}
          {device?.isPrimaryDevice && <span style={{ ...pillStyle, color: C.blue, background: C.blueLight, borderColor: C.blueLight }}>Primary</span>}
          {routingClaimsOf(device).length > 0 && (
            <span style={{ ...pillStyle, color: C.coralDark, background: C.coralLight, borderColor: C.coralLight }} title={formatChannelNames(routingClaimsOf(device), 99)}>
              {formatChannelNames(routingClaimsOf(device), 2)}
            </span>
          )}
        </div>
      </ModalHeader>
      <div style={{ borderBottom: `1px solid ${C.border}`, background: C.white, padding: "0 12px" }}>
        <Nav tabs style={{ borderBottom: "none" }}>{TABS.map(tabLink)}</Nav>
      </div>
      <ModalBody style={{ background: C.bg, padding: 20, fontFamily: "'Inter', sans-serif" }}>
        {activeTab === "overview" && renderOverview()}
        {activeTab === "routing" && renderRouting()}
        {activeTab === "alerts" && renderAlerts()}
        {activeTab === "analytics" && renderAnalytics()}
      </ModalBody>
      <ModalFooter style={{ borderTop: `1px solid ${C.borderLight}`, gap: 8 }}>
        {device?.isConnected && (
          <Button style={{ background: C.coral, border: "none", borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={onViewKds} disabled={isFetchingKds}>
            {isFetchingKds ? "Fetching..." : "View KDS & Printers"}
          </Button>
        )}
        <Button style={{ background: C.bg, border: `1px solid ${C.border}`, color: C.text, borderRadius: 10, fontWeight: 600, fontSize: 13 }} onClick={confirmClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};

export default DeviceDetailsModal;
